import type { ReactNode } from 'react'
import type { SipPlanRecommendationDetails } from '../../api/sipPlanRecommendation.types'
import {
  ConsistencyScorePointer,
  PowerRankPointer,
  RecencyScorePointer,
  VolatilityScorePointer
} from '../../../../plans-ui-components/PlanRecommendationScoreReasonPointers'
import { PlanRecommendationReasonPointer } from '../../../../plans-ui-components/PlanRecommendationReasonPointer'
import { planPercentageLabel } from '../../../../display-values/lumpsumPlanPercentage'
import { formatSipCategorySipsLabel } from '../../../../utils/sip/planListUtils'

type SipStartPlanRecommendationBodyProps = {
  planDetails: SipPlanRecommendationDetails | null
}

type SipStartReasonPointerKind =
  | 'MAINTAIN_EXPOSURE'
  | 'POWER_RANK'
  | 'CONSISTENCY_SCORE'
  | 'RECENCY_SCORE'
  | 'VOLATILITY_SCORE'

export function SipStartPlanRecommendationBody({
  planDetails
}: SipStartPlanRecommendationBodyProps) {
  const pointers = buildSipStartReasonPointers(planDetails)

  return (
    <div>
      <div className="h-[10px] bg-[#F0F4F7]" />
      <div className="pt-6 pb-2">
        <div className="px-5 pb-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
          Reasons to start SIP
        </div>
        {pointers.length > 0 ? <div className="pt-4">{pointers}</div> : null}
      </div>
    </div>
  )
}

function buildSipStartReasonPointers(
  planDetails: SipPlanRecommendationDetails | null
): ReactNode[] {
  const categoryName = (planDetails?.categoryName ?? '').trim() || 'this category'
  const categorySipsLabel = formatSipCategorySipsLabel(planDetails?.categoryName)
  const scoringData = planDetails?.scoringData ?? null
  const maxScore = scoringData?.maxScore ?? 100
  const idealAllocationAbsolute = planDetails?.idealAllocationPercentage?.absolute ?? 0
  const pointerKinds: SipStartReasonPointerKind[] = ['MAINTAIN_EXPOSURE']

  if (scoringData != null) {
    pointerKinds.push('POWER_RANK')
    if (scoringData?.consistencyScore != null) {
      pointerKinds.push('CONSISTENCY_SCORE')
    }
    if (scoringData?.recencyScore != null) {
      pointerKinds.push('RECENCY_SCORE')
    }
    if (scoringData?.volatilityScore != null) {
      pointerKinds.push('VOLATILITY_SCORE')
    }
  }

  return pointerKinds.map((pointerKind, pointerIndex) => {
    const index = pointerIndex + 1
    const borderBottom = pointerIndex < pointerKinds.length - 1

    if (pointerKind === 'MAINTAIN_EXPOSURE') {
      return (
        <PlanRecommendationReasonPointer
          key={pointerKind}
          index={index}
          title="Maintain ideal exposure"
          subtitle={`in ${categorySipsLabel}`}
          highlightDescription={`Adding SIP exposure in this fund helps you reach ideal allocation of ${planPercentageLabel({ rawPercentage: idealAllocationAbsolute })} in ${categoryName} as per your strategy.`}
        />
      )
    }

    if (pointerKind === 'POWER_RANK') {
      return (
        <PowerRankPointer
          key={pointerKind}
          index={index}
          borderBottom={borderBottom}
          categoryLabel={categorySipsLabel}
          powerRank={planDetails?.powerRank ?? null}
          powerRating={planDetails?.powerRating ?? ''}
          totalCategoryCount={planDetails?.totalCategoryCount ?? null}
        />
      )
    }

    if (pointerKind === 'CONSISTENCY_SCORE') {
      return (
        <ConsistencyScorePointer
          key={pointerKind}
          index={index}
          borderBottom={borderBottom}
          categoryLabel={categorySipsLabel}
          score={scoringData?.consistencyScore}
          maxScore={maxScore}
          performanceTag={scoringData?.consistencyScorePerformanceTag ?? ''}
        />
      )
    }

    if (pointerKind === 'RECENCY_SCORE') {
      return (
        <RecencyScorePointer
          key={pointerKind}
          index={index}
          borderBottom={borderBottom}
          categoryLabel={categorySipsLabel}
          score={scoringData?.recencyScore}
          maxScore={maxScore}
          performanceTag={scoringData?.recencyScorePerformanceTag ?? ''}
        />
      )
    }

    return (
      <VolatilityScorePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categorySipsLabel}
        score={scoringData?.volatilityScore}
        maxScore={maxScore}
        performanceTag={scoringData?.volatilityScorePerformanceTag ?? ''}
      />
    )
  })
}
